// This file is auto-generated by @hey-api/openapi-ts

import {
  type Client,
  formDataBodySerializer,
  type Options as Options2,
  type TDataShape,
} from "./client";
import { client } from "./client.gen";
import type {
  AcceptReportInviteData,
  AcceptReportInviteErrors,
  AcceptReportInviteResponses,
  AdminActivityActionsData,
  AdminActivityActionsResponses,
  AdminActivityAnalyticsData,
  AdminActivityAnalyticsErrors,
  AdminActivityAnalyticsResponses,
  AdminDeleteUserData,
  AdminDeleteUserErrors,
  AdminDeleteUserResponses,
  AdminGetUserData,
  AdminGetUserErrors,
  AdminGetUserResponses,
  AdminInviteUserData,
  AdminInviteUserErrors,
  AdminInviteUserResponses,
  AdminListActivityData,
  AdminListActivityErrors,
  AdminListActivityResponses,
  AdminListUsersData,
  AdminListUsersResponses,
  AdminUpdateUserData,
  AdminUpdateUserErrors,
  AdminUpdateUserResponses,
  AnalyzePrefillData,
  AnalyzePrefillErrors,
  AnalyzePrefillResponses,
  CalibrateAssessmentData,
  CalibrateAssessmentErrors,
  CalibrateAssessmentResponses,
  ChangePasswordData,
  ChangePasswordErrors,
  ChangePasswordResponses,
  CreateAssessmentData,
  CreateAssessmentErrors,
  CreateAssessmentResponses,
  CreateReportInviteData,
  CreateReportInviteErrors,
  CreateReportInviteResponses,
  DeleteAccountData,
  DeleteAccountErrors,
  DeleteAccountResponses,
  DeleteAssessmentData,
  DeleteAssessmentErrors,
  DeleteAssessmentResponses,
  GetActivityData,
  GetActivityResponses,
  GetAssessmentData,
  GetAssessmentDefinitionData,
  GetAssessmentDefinitionResponses,
  GetAssessmentErrors,
  GetAssessmentInternalData,
  GetAssessmentInternalErrors,
  GetAssessmentInternalResponses,
  GetAssessmentResponses,
  GetAuthProvidersData,
  GetAuthProvidersErrors,
  GetAuthProvidersResponses,
  GetHealthData,
  GetHealthResponses,
  GetMeData,
  GetMeDetailData,
  GetMeDetailResponses,
  GetMeResponses,
  GetReportInviteData,
  GetReportInviteErrors,
  GetReportInviteResponses,
  GetSharedAssessmentData,
  GetSharedAssessmentErrors,
  GetSharedAssessmentResponses,
  ListAssessmentsData,
  ListAssessmentsErrors,
  ListAssessmentsResponses,
  ListMyAssessmentsData,
  ListMyAssessmentsErrors,
  ListMyAssessmentsResponses,
  ListReportInvitesData,
  ListReportInvitesErrors,
  ListReportInvitesResponses,
  LoginData,
  LoginErrors,
  LoginResponses,
  LogoutData,
  LogoutResponses,
  OauthSessionData,
  OauthSessionErrors,
  OauthSessionResponses,
  OauthStartData,
  OauthStartErrors,
  RevokeReportInviteData,
  RevokeReportInviteErrors,
  RevokeReportInviteResponses,
  SetAssessmentShareData,
  SetAssessmentShareErrors,
  SetAssessmentShareResponses,
  SignupData,
  SignupErrors,
  SignupResponses,
  TrackAnalyticsEventData,
  TrackAnalyticsEventErrors,
  TrackAnalyticsEventResponses,
  UpdateProfileData,
  UpdateProfileErrors,
  UpdateProfileResponses,
} from "./types.gen";

export type Options<
  TData extends TDataShape = TDataShape,
  ThrowOnError extends boolean = boolean,
  TResponse = unknown,
> = Options2<TData, ThrowOnError, TResponse> & {
  /**
   * You can provide a client instance returned by `createClient()` instead of
   * individual options. This might be also useful if you want to implement a
   * custom client.
   */
  client?: Client;
  /**
   * You can pass arbitrary values through the `meta` object. This can be
   * used to access values that aren't defined as part of the SDK function.
   */
  meta?: Record<string, unknown>;
};

/**
 * Health
 *
 * Return a simple health status payload.
 */
export const getHealth = <ThrowOnError extends boolean = false>(
  options?: Options<GetHealthData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetHealthResponses, unknown, ThrowOnError>({
    url: "/health",
    ...options,
  });

/**
 * Auth Providers
 *
 * List the OAuth providers Supabase actually has enabled.
 *
 * Public and unauthenticated, mirroring GET /health. The frontend renders one
 * sign-in button per returned provider, so a provider toggled off in the
 * Supabase dashboard disappears from the UI instead of erroring on click — the
 * exact bug this endpoint exists to prevent. Fails CLOSED: any upstream error
 * is a 502, never a "both providers work" guess that would reintroduce it.
 */
export const getAuthProviders = <ThrowOnError extends boolean = false>(
  options?: Options<GetAuthProvidersData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetAuthProvidersResponses, GetAuthProvidersErrors, ThrowOnError>({
    url: "/auth/providers",
    ...options,
  });

/**
 * Auth Signup
 *
 * Register a new user with email verification.
 *
 * Signup is public — the ALLOWED_EMAIL_DOMAIN gate applies only to
 * admin-issued invites (POST /admin/invite), not self-registration.
 */
export const signup = <ThrowOnError extends boolean = false>(
  options: Options<SignupData, ThrowOnError>,
) =>
  (options.client ?? client).post<SignupResponses, SignupErrors, ThrowOnError>({
    url: "/auth/signup",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Login
 *
 * Authenticate via Supabase, then set the signed session cookie.
 *
 * The Supabase access/refresh tokens never leave the backend — the browser
 * only ever sees the HMAC cookie this endpoint sets.
 */
export const login = <ThrowOnError extends boolean = false>(
  options: Options<LoginData, ThrowOnError>,
) =>
  (options.client ?? client).post<LoginResponses, LoginErrors, ThrowOnError>({
    url: "/auth/login",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Oauth Start
 *
 * Redirect the browser to Supabase's OAuth authorize endpoint.
 *
 * The provider must be enabled in Supabase → Auth → Providers, and
 * `{FRONTEND_URL}/auth/callback` must be present in Supabase's redirect
 * allow-list, or Supabase will reject the callback. After the provider
 * round-trip, the frontend posts the resulting access token to
 * POST /auth/oauth/session to obtain the signed session cookie.
 */
export const oauthStart = <ThrowOnError extends boolean = false>(
  options: Options<OauthStartData, ThrowOnError>,
) =>
  (options.client ?? client).get<unknown, OauthStartErrors, ThrowOnError>({
    url: "/auth/oauth/{provider}",
    ...options,
  });

/**
 * Auth Oauth Session
 *
 * Exchange a Supabase OAuth access token for the signed session cookie.
 *
 * The frontend completes the provider redirect client-side and posts the
 * resulting Supabase access token here. The token is verified server-side and
 * the browser only ever receives the HMAC cookie — the Supabase token never
 * round-trips back to it.
 */
export const oauthSession = <ThrowOnError extends boolean = false>(
  options: Options<OauthSessionData, ThrowOnError>,
) =>
  (options.client ?? client).post<OauthSessionResponses, OauthSessionErrors, ThrowOnError>({
    url: "/auth/oauth/session",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Logout
 *
 * Sign out — clear the session cookie.
 */
export const logout = <ThrowOnError extends boolean = false>(
  options?: Options<LogoutData, ThrowOnError>,
) =>
  (options?.client ?? client).post<LogoutResponses, unknown, ThrowOnError>({
    url: "/auth/logout",
    ...options,
  });

/**
 * Auth Me
 *
 * Get the current user's profile.
 */
export const getMe = <ThrowOnError extends boolean = false>(
  options?: Options<GetMeData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetMeResponses, unknown, ThrowOnError>({
    url: "/auth/me",
    ...options,
  });

/**
 * Auth Me Detail
 *
 * Get the current user's profile with timeline fields.
 */
export const getMeDetail = <ThrowOnError extends boolean = false>(
  options?: Options<GetMeDetailData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetMeDetailResponses, unknown, ThrowOnError>({
    url: "/auth/me/detail",
    ...options,
  });

/**
 * Auth Update Profile
 *
 * Update current user's name.
 */
export const updateProfile = <ThrowOnError extends boolean = false>(
  options: Options<UpdateProfileData, ThrowOnError>,
) =>
  (options.client ?? client).put<UpdateProfileResponses, UpdateProfileErrors, ThrowOnError>({
    url: "/auth/profile",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Change Password
 *
 * Change the current user's password (verifies current password first).
 */
export const changePassword = <ThrowOnError extends boolean = false>(
  options: Options<ChangePasswordData, ThrowOnError>,
) =>
  (options.client ?? client).put<ChangePasswordResponses, ChangePasswordErrors, ThrowOnError>({
    url: "/auth/password",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Delete Account
 *
 * Delete the caller's own account and ERASE every assessment they own.
 *
 * Self-service counterpart to admin user deletion, with the same erase
 * semantics. Confirmation depends on how the account signs in: an
 * email/password account must re-enter its current password (verified against
 * Supabase — any failure is a 400, mirroring change-password), while an
 * OAuth-only account has no password and is allowed to delete without one
 * (future-proofing BOA-49).
 *
 * The steps are ordered so nothing is left orphaned: the caller's assessments
 * are deleted first (their user_id FK is ON DELETE SET NULL, so deleting the
 * auth user alone would only detach them), then the auth user is deleted —
 * cascading their profile, activity_logs, and any report_access grants — and
 * finally the session cookie is cleared on the response.
 */
export const deleteAccount = <ThrowOnError extends boolean = false>(
  options: Options<DeleteAccountData, ThrowOnError>,
) =>
  (options.client ?? client).post<DeleteAccountResponses, DeleteAccountErrors, ThrowOnError>({
    url: "/auth/delete-account",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Activity
 *
 * Get the current user's activity log.
 */
export const getActivity = <ThrowOnError extends boolean = false>(
  options?: Options<GetActivityData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetActivityResponses, unknown, ThrowOnError>({
    url: "/auth/activity",
    ...options,
  });

/**
 * Admin List Users
 *
 * List all users (admin only).
 */
export const adminListUsers = <ThrowOnError extends boolean = false>(
  options?: Options<AdminListUsersData, ThrowOnError>,
) =>
  (options?.client ?? client).get<AdminListUsersResponses, unknown, ThrowOnError>({
    url: "/admin/users",
    ...options,
  });

/**
 * Admin Delete User
 *
 * Delete a user (admin only). Self-deletion blocked.
 *
 * Adopts the same ERASE semantics as self-service account deletion: the
 * target's assessments are deleted first (their user_id FK is ON DELETE SET
 * NULL, so deleting the auth user alone would detach rather than remove them),
 * then the auth user is deleted — cascading their profile, activity_logs, and
 * any report_access grants. The action is audited against the admin, who
 * survives the deletion; the detail carries the erased count (no personal data).
 */
export const adminDeleteUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminDeleteUserData, ThrowOnError>,
) =>
  (options.client ?? client).delete<AdminDeleteUserResponses, AdminDeleteUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
  });

/**
 * Admin Get User
 *
 * Get a single user detail with activity logs (admin only).
 */
export const adminGetUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminGetUserData, ThrowOnError>,
) =>
  (options.client ?? client).get<AdminGetUserResponses, AdminGetUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
  });

/**
 * Admin Update User
 *
 * Update a user's name or role (admin only). Self-demotion blocked.
 */
export const adminUpdateUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminUpdateUserData, ThrowOnError>,
) =>
  (options.client ?? client).put<AdminUpdateUserResponses, AdminUpdateUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Admin Invite User
 *
 * Invite a user by email (admin only).
 */
export const adminInviteUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminInviteUserData, ThrowOnError>,
) =>
  (options.client ?? client).post<AdminInviteUserResponses, AdminInviteUserErrors, ThrowOnError>({
    url: "/admin/invite",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Admin List Activity
 *
 * List activity across all users (admin only).
 *
 * Search, filtering, sorting, and pagination are all applied server-side by
 * the list_recent_activity RPC — the client sends params and renders results.
 */
export const adminListActivity = <ThrowOnError extends boolean = false>(
  options?: Options<AdminListActivityData, ThrowOnError>,
) =>
  (options?.client ?? client).get<
    AdminListActivityResponses,
    AdminListActivityErrors,
    ThrowOnError
  >({ url: "/admin/activity", ...options });

/**
 * Admin Activity Analytics
 *
 * Aggregated activity analytics for the admin dashboard (admin only).
 */
export const adminActivityAnalytics = <ThrowOnError extends boolean = false>(
  options?: Options<AdminActivityAnalyticsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<
    AdminActivityAnalyticsResponses,
    AdminActivityAnalyticsErrors,
    ThrowOnError
  >({ url: "/admin/activity/analytics", ...options });

/**
 * Admin Activity Actions
 *
 * Distinct activity actions across all time (admin only).
 *
 * Used to populate the activity filter dropdown so it matches the full,
 * unbounded table scope rather than the windowed analytics breakdown.
 */
export const adminActivityActions = <ThrowOnError extends boolean = false>(
  options?: Options<AdminActivityActionsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<AdminActivityActionsResponses, unknown, ThrowOnError>({
    url: "/admin/activity/actions",
    ...options,
  });

/**
 * Assessment Definition
 *
 * Return the full question bank grouped by layer and dimension.
 *
 * The engine is server-side only — the frontend renders the wizard from
 * this definition rather than a local copy of the question bank.
 */
export const getAssessmentDefinition = <ThrowOnError extends boolean = false>(
  options?: Options<GetAssessmentDefinitionData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetAssessmentDefinitionResponses, unknown, ThrowOnError>({
    url: "/assessment/definition",
    ...options,
  });

/**
 * Assessments List
 *
 * List the 200 most recent assessments for the expert queue (BOI staff only).
 */
export const listAssessments = <ThrowOnError extends boolean = false>(
  options?: Options<ListAssessmentsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<ListAssessmentsResponses, ListAssessmentsErrors, ThrowOnError>({
    url: "/assessments",
    ...options,
  });

/**
 * Assessments Create
 *
 * Create, score, and persist a new assessment for the logged-in user.
 *
 * Requires a session: the assessment is always attributed to the caller's
 * profile id so it shows up in their /assessments/mine history and gates the
 * report link to owner/admin reads.
 */
export const createAssessment = <ThrowOnError extends boolean = false>(
  options: Options<CreateAssessmentData, ThrowOnError>,
) =>
  (options.client ?? client).post<CreateAssessmentResponses, CreateAssessmentErrors, ThrowOnError>({
    url: "/assessments",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Assessments List Mine
 *
 * List the logged-in user's own assessments (history page).
 */
export const listMyAssessments = <ThrowOnError extends boolean = false>(
  options?: Options<ListMyAssessmentsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<
    ListMyAssessmentsResponses,
    ListMyAssessmentsErrors,
    ThrowOnError
  >({ url: "/assessments/mine", ...options });

/**
 * Shared Assessment Get
 *
 * Public, anonymous, view-only report resolved by an unguessable token.
 *
 * Unknown, revoked, and ownerless tokens all return the same 404, so the
 * endpoint is not an enumeration oracle. Serves the ORIGINAL system report and
 * scores only — never the expert-calibrated regeneration. The view is logged
 * against the report OWNER, because the viewer is anonymous and
 * activity_logs.user_id is NOT NULL by design.
 */
export const getSharedAssessment = <ThrowOnError extends boolean = false>(
  options: Options<GetSharedAssessmentData, ThrowOnError>,
) =>
  (options.client ?? client).get<
    GetSharedAssessmentResponses,
    GetSharedAssessmentErrors,
    ThrowOnError
  >({ url: "/assessments/shared/{share_token}", ...options });

/**
 * Assessments Delete
 *
 * Delete one assessment the caller owns (owner only).
 *
 * Deliberately owner-only — admins are NOT allowed, matching the share and
 * invite management gates: erasing a report is the data owner's decision, not
 * an operational one. The delete cascades to the row's calibrations and
 * report_access invites, and the share token is a column on the row, so nothing
 * is left behind. The audit detail is the bare id — never company or respondent
 * data.
 */
export const deleteAssessment = <ThrowOnError extends boolean = false>(
  options: Options<DeleteAssessmentData, ThrowOnError>,
) =>
  (options.client ?? client).delete<
    DeleteAssessmentResponses,
    DeleteAssessmentErrors,
    ThrowOnError
  >({ url: "/assessments/{assessment_id}", ...options });

/**
 * Assessments Get
 *
 * Fetch one assessment with its result, report, and calibration count.
 *
 * Access is restricted to three parties: the assessment's OWNER, BOI STAFF (DB
 * role "user" or "admin"), or a GRANTEE who redeemed an invite for this report
 * (BOA-39). The caller's role and grant are both re-read from the DB (never
 * trusted from the cookie), so demoting a staffer to "external" or revoking an
 * invite takes effect on the very next request. Legacy rows with a NULL owner
 * remain staff-only.
 *
 * The response never includes raw calibration payloads (internal expert
 * feedback), and the `share` block — which carries the live public share token
 * — is populated only for the owner. Neither a staffer reading someone else's
 * report nor an invited grantee ever receives it: a grantee was given a read,
 * not the right to publish the report to the world.
 */
export const getAssessment = <ThrowOnError extends boolean = false>(
  options: Options<GetAssessmentData, ThrowOnError>,
) =>
  (options.client ?? client).get<GetAssessmentResponses, GetAssessmentErrors, ThrowOnError>({
    url: "/assessments/{assessment_id}",
    ...options,
  });

/**
 * Assessments Set Share
 *
 * Enable or disable the public view-only share link (owner only).
 *
 * Deliberately owner-only — unlike the owner-OR-admin read gate — because
 * publishing a report to a world-readable link is the data owner's consent
 * decision. Idempotent: off->on mints a fresh token, enabling an already-shared
 * report keeps it, and disabling revokes for real (token -> NULL). Only a
 * genuine state transition is logged, so toggling back and forth or re-sending
 * the current state does not flood activity_logs.
 */
export const setAssessmentShare = <ThrowOnError extends boolean = false>(
  options: Options<SetAssessmentShareData, ThrowOnError>,
) =>
  (options.client ?? client).put<
    SetAssessmentShareResponses,
    SetAssessmentShareErrors,
    ThrowOnError
  >({
    url: "/assessments/{assessment_id}/share",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Report Invites List
 *
 * List every invite issued for this report, newest first (owner only).
 *
 * Cleartext `invite_token` rides along so the owner can re-copy a link they
 * lost, and is therefore non-null on pending rows only — accepted and revoked
 * invites have had it NULLed in the database, not merely hidden here.
 */
export const listReportInvites = <ThrowOnError extends boolean = false>(
  options: Options<ListReportInvitesData, ThrowOnError>,
) =>
  (options.client ?? client).get<ListReportInvitesResponses, ListReportInvitesErrors, ThrowOnError>(
    { url: "/assessments/{assessment_id}/invites", ...options },
  );

/**
 * Report Invite Create
 *
 * Invite one colleague, by email, to read this report (owner only).
 *
 * The minted token is a pointer, not a credential: it grants nothing until the
 * invited address signs in and redeems it. Idempotent — re-inviting an address
 * that already holds a live invite returns that invite rather than rotating its
 * token (which would break a link already sent), and an address that has
 * already accepted returns its accepted row. Only a genuinely new invite is
 * audited, so a double-submitted form does not flood activity_logs.
 *
 * When email is configured, the invite link is delivered to the invitee — for
 * both a fresh mint and an idempotent re-invite of a pending address (a
 * re-invite is a free "resend"). Delivery is best-effort: a send failure is
 * reported via `email_delivery` but never fails invite creation.
 */
export const createReportInvite = <ThrowOnError extends boolean = false>(
  options: Options<CreateReportInviteData, ThrowOnError>,
) =>
  (options.client ?? client).post<
    CreateReportInviteResponses,
    CreateReportInviteErrors,
    ThrowOnError
  >({
    url: "/assessments/{assessment_id}/invites",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Report Invite Revoke
 *
 * Revoke one invite (owner only).
 *
 * Revocation is real in both directions: an unopened link stops working, and a
 * colleague who had already accepted loses the report on their next request,
 * because the revoke NULLs the grantee_user_id that the read gate resolves. The
 * invite is scoped to this assessment, so an id belonging to another report is
 * a 404 rather than a cross-report revoke.
 */
export const revokeReportInvite = <ThrowOnError extends boolean = false>(
  options: Options<RevokeReportInviteData, ThrowOnError>,
) =>
  (options.client ?? client).delete<
    RevokeReportInviteResponses,
    RevokeReportInviteErrors,
    ThrowOnError
  >({ url: "/assessments/{assessment_id}/invites/{invite_id}", ...options });

/**
 * Assessments Get Internal
 *
 * Fetch the expert-only view of an assessment (BOI staff only).
 *
 * Includes the raw calibration payloads and — once calibrated — the
 * internally regenerated report, neither of which may appear on the
 * public getAssessment record.
 */
export const getAssessmentInternal = <ThrowOnError extends boolean = false>(
  options: Options<GetAssessmentInternalData, ThrowOnError>,
) =>
  (options.client ?? client).get<
    GetAssessmentInternalResponses,
    GetAssessmentInternalErrors,
    ThrowOnError
  >({ url: "/assessments/{assessment_id}/internal", ...options });

/**
 * Assessments Calibrate
 *
 * Apply an expert calibration to an assessment (BOI staff only).
 */
export const calibrateAssessment = <ThrowOnError extends boolean = false>(
  options: Options<CalibrateAssessmentData, ThrowOnError>,
) =>
  (options.client ?? client).post<
    CalibrateAssessmentResponses,
    CalibrateAssessmentErrors,
    ThrowOnError
  >({
    url: "/assessments/{assessment_id}/calibrate",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Report Invite Get
 *
 * Preview an invite before redeeming it (invited address only).
 *
 * An account is required: the session email is the credential the invite is
 * bound to. The preview carries report METADATA only — the company name, never
 * the scores or the report body — because the caller has not accepted yet, and
 * `expired` lets the UI explain a dead invite instead of failing on Accept.
 */
export const getReportInvite = <ThrowOnError extends boolean = false>(
  options: Options<GetReportInviteData, ThrowOnError>,
) =>
  (options.client ?? client).get<GetReportInviteResponses, GetReportInviteErrors, ThrowOnError>({
    url: "/report-invites/{token}",
    ...options,
  });

/**
 * Report Invite Accept
 *
 * Redeem an invite: bind this report to the caller's account (single-use).
 *
 * Redeeming is the only write that grants a read. It stamps the caller as the
 * grantee and NULLs the token, so the link in their inbox dies on first use and
 * cannot be replayed by whoever reads that mailbox next. An expired invite is a
 * 410 — distinct from the 404s, because the caller has already proved they are
 * the invitee and deserves to know why it failed. The acceptance is audited
 * against the GRANTEE, who is authenticated and can be attributed honestly.
 */
export const acceptReportInvite = <ThrowOnError extends boolean = false>(
  options: Options<AcceptReportInviteData, ThrowOnError>,
) =>
  (options.client ?? client).post<
    AcceptReportInviteResponses,
    AcceptReportInviteErrors,
    ThrowOnError
  >({ url: "/report-invites/{token}/accept", ...options });

/**
 * Prefill Analyze
 *
 * Extract text from uploads/paste/URL and suggest prefilled answers.
 *
 * Per-file extraction failures never fail the request — they surface as an
 * `error` on the corresponding document entry.
 */
export const analyzePrefill = <ThrowOnError extends boolean = false>(
  options?: Options<AnalyzePrefillData, ThrowOnError>,
) =>
  (options?.client ?? client).post<AnalyzePrefillResponses, AnalyzePrefillErrors, ThrowOnError>({
    ...formDataBodySerializer,
    url: "/prefill",
    ...options,
    headers: {
      "Content-Type": null,
      ...options?.headers,
    },
  });

/**
 * Analytics Track Event
 *
 * Record one anonymous product-analytics funnel event.
 *
 * Deliberately anonymous: this handler has NO session dependency and never
 * reads the auth cookie, so an event can't be linked to an account even by
 * accident. The payload is allow-listed by AnalyticsEventRequest, keeping the
 * captured detail coarse and non-identifying by construction. The client fires
 * and forgets, so a DB failure surfaces as a 500 rather than being swallowed.
 */
export const trackAnalyticsEvent = <ThrowOnError extends boolean = false>(
  options: Options<TrackAnalyticsEventData, ThrowOnError>,
) =>
  (options.client ?? client).post<
    TrackAnalyticsEventResponses,
    TrackAnalyticsEventErrors,
    ThrowOnError
  >({
    url: "/analytics/events",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
